import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Mail, FileText } from 'lucide-react';

export default function TermsOfService() {
  const navigate = useNavigate();

  const sections = [
    {
      title: "1. Introduction",
      content: `These Terms of Service govern the use of the BodyCorp365 website, mobile application, and related services.

By accessing or using the BodyCorp365 platform, users agree to comply with these Terms of Service. If you do not agree with these terms, you should not use the platform.

BodyCorp365 provides a digital platform designed to facilitate communication, administration, and maintenance management for residential complexes.`
    },
    {
      title: "2. Definitions",
      content: "For the purposes of these Terms:",
      definitions: [
        { term: "Platform", definition: "refers to the BodyCorp365 website, mobile application, and related services." },
        { term: "Users", definition: "refer to any individuals or entities accessing or using the platform, including residents, property owners, trustees, managing agents, and contractors." },
        { term: "Complex", definition: "refers to a residential building, sectional title scheme, estate, or community registered on the platform." },
      ]
    },
    {
      title: "3. User Accounts",
      content: `Users may be required to create an account to access certain features of the platform.

Users agree to:`,
      items: [
        "Provide accurate and current information during registration",
        "Maintain the confidentiality of their login credentials",
        "Notify BodyCorp365 immediately of any unauthorised use of their account"
      ],
      note: "Users are responsible for all activities conducted through their accounts."
    },
    {
      title: "4. Platform Use",
      content: "BodyCorp365 provides tools for:",
      items: [
        "Resident communication",
        "Maintenance request management",
        "Document storage",
        "Contractor quote requests",
        "Administrative support for trustees and managing agents"
      ],
      additionalContent: `Users agree to use the platform only for lawful purposes and in a manner consistent with residential community management.

Users may not use the platform to:`,
      prohibitedItems: [
        "Upload false or misleading information",
        "Harass or threaten other users",
        "Distribute unlawful or offensive content",
        "Attempt to disrupt the operation or security of the platform"
      ]
    },
    {
      title: "5. Contractor Marketplace",
      content: `BodyCorp365 may provide a marketplace where contractors can submit quotes and provide services to residential complexes.

BodyCorp365:`,
      items: [
        "Does not guarantee the quality of contractor services",
        "Does not act as a contractor or service provider",
        "Is not responsible for disputes between complexes and contractors"
      ],
      note: `Complexes are responsible for evaluating and approving contractors before any work is undertaken.

A service fee may be applied to contractor jobs processed through the platform as specified in the applicable pricing model.`
    },
    {
      title: "6. Managing Agent and Trustee Responsibilities",
      content: "Trustees and managing agents using the platform remain responsible for:",
      items: [
        "Managing the affairs of the residential complex",
        "Ensuring compliance with applicable laws and governance requirements",
        "Approving maintenance work and contractor engagements"
      ],
      note: "The platform provides tools to assist with administration but does not replace the legal responsibilities of trustees or managing agents."
    },
    {
      title: "7. Payment and Subscriptions",
      content: `Access to certain features of the BodyCorp365 platform may require payment of subscription fees.

Subscription terms may include:`,
      items: [
        "Monthly or annual billing",
        "Online payment or bank transfer",
        "Invoice-based payments where applicable"
      ],
      note: "Failure to maintain active subscription payments may result in restricted platform access."
    },
    {
      title: "8. Data Protection and Privacy",
      content: `BodyCorp365 processes personal information in accordance with its Privacy Policy and applicable data protection laws, including the Protection of Personal Information Act.

By using the platform, users consent to the collection and processing of their information as described in the Privacy Policy.`,
      hasPrivacyLink: true
    },
    {
      title: "9. Intellectual Property",
      content: `All content, software, design, branding, and technology associated with the BodyCorp365 platform remain the intellectual property of BodyCorp365.

Users may not copy, reproduce, distribute, or reverse-engineer any part of the platform without written permission.`
    },
    {
      title: "10. Limitation of Liability",
      content: `BodyCorp365 provides the platform on an "as available" basis.

While reasonable efforts are made to maintain platform reliability, BodyCorp365 does not guarantee uninterrupted access or error-free functionality.

BodyCorp365 will not be liable for:`,
      items: [
        "Losses arising from platform downtime",
        "Disputes between users",
        "Contractor performance or service quality",
        "Any indirect or consequential damages resulting from platform use"
      ]
    },
    {
      title: "11. Account Suspension or Termination",
      content: "BodyCorp365 reserves the right to suspend or terminate user accounts if users:",
      items: [
        "Violate these Terms of Service",
        "Engage in unlawful activities",
        "Misuse the platform"
      ],
      note: "Termination may occur with or without prior notice where necessary to protect the platform or its users."
    },
    {
      title: "12. Changes to Terms",
      content: `BodyCorp365 may update these Terms of Service from time to time.

Continued use of the platform after updates constitutes acceptance of the revised terms.`
    },
    {
      title: "13. Governing Law",
      content: `These Terms of Service are governed by the laws of South Africa.

Any disputes arising from the use of the platform shall be subject to the jurisdiction of South African courts.`
    },
    {
      title: "14. Contact Information",
      content: "For questions regarding these Terms of Service, please contact:",
      contact: true
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div 
              className="flex items-center gap-2 cursor-pointer" 
              onClick={() => navigate('/')}
            >
              <img 
                src="/logo.png" 
                alt="BodyCorp365" 
                className="h-10 w-auto"
              />
              <span className="text-xl font-bold font-manrope text-slate-900">BodyCorp365</span>
            </div>
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
        </div>
      </nav>

      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
              <FileText className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold font-manrope text-slate-900">Terms of Service</h1>
              <p className="text-slate-500">Last Updated: 14 March 2026</p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-8 sm:p-12 space-y-10">
            {sections.map((section, index) => (
              <section key={index} className="scroll-mt-20" id={`section-${index + 1}`}>
                <h2 className="text-xl font-bold text-slate-900 mb-4">{section.title}</h2>
                
                {section.content && (
                  <p className="text-slate-600 leading-relaxed whitespace-pre-line mb-4">
                    {section.content}
                  </p>
                )}

                {section.definitions && (
                  <div className="space-y-3 ml-4 mb-4">
                    {section.definitions.map((def, defIndex) => (
                      <div key={defIndex}>
                        <span className="font-semibold text-slate-800">{def.term}</span>
                        <span className="text-slate-600"> {def.definition}</span>
                      </div>
                    ))}
                  </div>
                )}

                {section.items && (
                  <ul className="list-disc list-inside space-y-2 text-slate-600 ml-4 mb-4">
                    {section.items.map((item, itemIndex) => (
                      <li key={itemIndex}>{item}</li>
                    ))}
                  </ul>
                )}

                {section.additionalContent && (
                  <p className="text-slate-600 leading-relaxed whitespace-pre-line mb-4">
                    {section.additionalContent}
                  </p>
                )}

                {section.prohibitedItems && (
                  <ul className="list-disc list-inside space-y-2 text-slate-600 ml-4 mb-4">
                    {section.prohibitedItems.map((item, itemIndex) => (
                      <li key={itemIndex}>{item}</li>
                    ))}
                  </ul>
                )}

                {section.note && (
                  <p className="text-slate-500 text-sm mt-4 italic whitespace-pre-line">{section.note}</p>
                )}

                {section.hasPrivacyLink && (
                  <div className="mt-4">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate('/privacy')}
                    >
                      View Privacy Policy
                    </Button>
                  </div>
                )}

                {section.contact && (
                  <div className="bg-slate-50 rounded-xl p-6 mt-4">
                    <h3 className="font-semibold text-slate-800 mb-3">BodyCorp365 Support</h3>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-slate-600">
                        <Mail className="w-4 h-4 text-emerald-600" />
                        <a href="mailto:admin@bodycorp365.com" className="text-emerald-600 hover:underline">
                          admin@bodycorp365.com
                        </a>
                      </div>
                      <div className="flex items-center gap-2 text-slate-600">
                        <span className="text-slate-500">Website:</span>
                        <a href="https://www.bodycorp365.com" className="text-emerald-600 hover:underline">
                          www.bodycorp365.com
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </section>
            ))}
          </div>
        </div>

        {/* Back to top */}
        <div className="text-center mt-8">
          <Button variant="outline" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            Back to Top
          </Button>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center text-slate-500 text-sm">
          © {new Date().getFullYear()} BodyCorp365. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
